import { useEffect } from "react";
import { FilterPoint, NB_POINTS_MAX } from "../../types/types";
import { useLocation, useNavigate } from "react-router-dom";

import { getTodayDate } from "../../utils/dateUtils";
import { getFilterFromURL, setFilterInURL } from "../../utils/filter.utils";
import { useDispatch, useSelector } from "react-redux";
import { filterMapState } from "../../redux/filter.mapstate";
import { setFilterAction } from "../../redux/filter.actions";
import { calculateDifferenceDate } from "../../../utils/time.utils";

export interface filterOptionProps {
  idFilter ? : string
}
export default function useFilterAnalysisCore(options: filterOptionProps = {}) {
  const location: string = useLocation().search;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const filter = useSelector(filterMapState).filter.filter;

  const setFilterRaw = (filter: FilterPoint) => {
    dispatch(setFilterAction(filter));
  };

  const setFilter = (filter: FilterPoint) => {
    const reformatefilter = { ...filter };

    if (filter.startDate! && filter.stopDate!) {
      const nbHours = calculateDifferenceDate(
        new Date(filter.startDate),
        new Date(filter.stopDate),
        "h"
      );
      const nbDays = calculateDifferenceDate(
        new Date(filter.startDate),
        new Date(filter.stopDate),
        "d"
      );
      const nbMonths = calculateDifferenceDate(
        new Date(filter.startDate),
        new Date(filter.stopDate),
        "mo"
      );
      if (nbHours > NB_POINTS_MAX) {
        if (filter.aggregateWindow === "1h") {
          reformatefilter.aggregateWindow = "1d";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);
          return;
        }
      }

      if (nbDays > NB_POINTS_MAX) {
        if (
          filter.aggregateWindow === "1d" ||
          filter.aggregateWindow === "1h"
        ) {
          reformatefilter.aggregateWindow = "1mo";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);

          return;
        }
      }

      if (nbDays <= 1) {
        if (
          filter.aggregateWindow === "1d" ||
          filter.aggregateWindow === "1mo"
        ) {
          reformatefilter.aggregateWindow = "1h";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);
          return;
        }
      } else if (nbMonths <= 1) {
        if (filter.aggregateWindow === "1mo") {
          reformatefilter.aggregateWindow = "1d";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);
        }
      }
    }

    setFilterInURL(reformatefilter, location, navigate);
    setFilterRaw(reformatefilter);
  };

  const handlePrevWindow = () => {
    const aggregateWindow = filter.aggregateWindow;
    const startDate = filter.startDate || new Date().toISOString();

    if (aggregateWindow === "1h") {
      setFilter({
        ...filter,
        startDate: getTodayDate({
          currentDate: startDate,
          resetHour: true,
          nbDay: -1,
        }),
        stopDate: getTodayDate({
          currentDate: startDate,
          resetHour: true,
          nbHour: +1,
        }),
        aggregateWindow,
      });
      return;
    }

    if (aggregateWindow === "1d") {
      setFilter({
        ...filter,
        startDate: getTodayDate({
          currentDate: startDate,
          nbMonth: -1,
          resetDay: true,
          resetHour: true,
        }),
        stopDate: getTodayDate({
          currentDate: startDate,
          resetDay: true,
          resetHour: true,
        }),
        aggregateWindow,
      });
      return;
    }

    if (aggregateWindow === "1mo") {
      setFilter({
        ...filter,
        startDate: getTodayDate({
          currentDate: startDate,
          nbYear: -1,
          resetMonth: true,
        }),
        stopDate: getTodayDate({ currentDate: startDate, resetMonth: true }),
        aggregateWindow,
      });
      return;
    }
  };

  const handleNextWindow = () => {
    const aggregateWindow = filter?.aggregateWindow;
    const startDate = filter?.stopDate || new Date().toISOString();

    if (aggregateWindow === "1h") {
      setFilter({
        ...filter,
        startDate: getTodayDate({ currentDate: startDate, resetHour: true }),
        stopDate: getTodayDate({
          currentDate: startDate,
          resetHour: true,
          nbDay: +1,
          nbHour: +1,
        }),
        aggregateWindow,
      });
      return;
    }

    if (aggregateWindow === "1d") {
      setFilter({
        ...filter,
        startDate: getTodayDate({
          currentDate: startDate,
          resetHour: true,
          resetDay: true,
        }),
        stopDate: getTodayDate({
          currentDate: startDate,
          resetDay: true,
          nbMonth: +1,
          resetHour: true,
        }),
        aggregateWindow,
      });
      return;
    }

    if (aggregateWindow === "1mo") {
      setFilter({
        ...filter,
        startDate: getTodayDate({ currentDate: startDate, resetMonth: true }),
        stopDate: getTodayDate({
          currentDate: startDate,
          nbYear: 1,
          resetMonth: true,
        }),
        aggregateWindow,
      });
      return;
    }
  };

  useEffect(() => {
    const filterURL = getFilterFromURL(location);

    const initialeFilter: FilterPoint = {
      startDate: getTodayDate({ resetMonth: true }),
      stopDate: getTodayDate({ nbYear: 1, resetMonth: true }),

      aggregateWindow: "1mo",
    };

    if (
      filterURL.startDate === undefined ||
      filterURL.stopDate === undefined ||
      filterURL.aggregateWindow === undefined
    ) {
      setFilter(initialeFilter);
      return;
    } else {
      setFilter(filterURL);
    }
    // Do not remove the comment below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  return {
    setFilter,
    filter,
    handleNextWindow,
    handlePrevWindow,
  };
}



export const useGetAnalysisFilter = () => {
    const filter = useSelector(filterMapState).filter.filter;
    return {
      filter
    }
}